import React from 'react';
import { AppButton, AppMultiSelect, SearchTextField } from '@/components/atoms';
import { Search, Download } from '@/components/atoms/AppIcon';
import { RA2NDatePicker } from '@/components/organisms';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { JA_JP } from '@/components/organisms/RA2NDatePicker/constants';
import { parseDate, parseFormat } from '@/components/organisms/RA2NDatePicker/datepickerLogic';
import type { TenantSiteTypeItem } from '@/services/tenants';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2, SERVICE_TYPE_LABELS } from '@/constants/serviceType';

const dayFormat = parseFormat(JA_JP.format);

function ra2nDateToYYYYMMDD(value: string): string {
  if (!value?.trim()) return '';
  const d = parseDate(value, dayFormat);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

const SERVICE_TYPE_OPTIONS = [
  { label: 'すべて', value: 'all' },
  { label: `${SERVICE_TYPE_A2} (${SERVICE_TYPE_LABELS[SERVICE_TYPE_A2]})`, value: SERVICE_TYPE_A2 },
  { label: `${SERVICE_TYPE_R2} (${SERVICE_TYPE_LABELS[SERVICE_TYPE_R2]})`, value: SERVICE_TYPE_R2 },
];

type InformationsFilterPanelProps = {
  // 表示対象 (site type)
  canUseSiteTypeFeatures: boolean;
  tenants: TenantSiteTypeItem[];
  siteTypes: string[];
  hasAllSiteTypesSelected: boolean;
  onChangeSiteTypes: (next: string[]) => void;

  // タイトル
  searchTitle: string;
  onChangeSearchTitle: (next: string) => void;

  // お知らせ内容
  searchContents: string;
  onChangeSearchContents: (next: string) => void;

  // 有効期限
  availableDate: string;
  onChangeAvailableDate: (next: string) => void;

  // 表示サービス (service type)
  serviceTypes: string[];
  hasAllServiceTypesSelected: boolean;
  onChangeServiceTypes: (next: string[]) => void;

  onSearch: () => void;
  onCsv: () => void;
};

const InformationsFilterPanel: React.FC<InformationsFilterPanelProps> = ({
  canUseSiteTypeFeatures,
  tenants,
  siteTypes,
  hasAllSiteTypesSelected,
  onChangeSiteTypes,
  searchTitle,
  onChangeSearchTitle,
  searchContents,
  onChangeSearchContents,
  availableDate,
  onChangeAvailableDate,
  serviceTypes,
  hasAllServiceTypesSelected,
  onChangeServiceTypes,
  onSearch,
  onCsv,
}) => {
  const { colors } = useTheme();
  const tenantSiteTypes = tenants.map(t => t.site_type).filter(Boolean) as string[];
  const allServiceValues = [SERVICE_TYPE_A2, SERVICE_TYPE_R2];

  return (
    <div className="bg-white rounded-md border p-4 mb-4">
      {/* Row 1: first 3 inputs */}
      <div className="grid gap-4 grid-cols-4 items-end pb-4">
        <div>
          <label htmlFor="available_date" className="mb-1 block text-sm text-gray-600">
            基準日
          </label>
          <RA2NDatePicker
            picker="day"
            className="w-full"
            value={availableDate || ''}
            onChange={v => onChangeAvailableDate(ra2nDateToYYYYMMDD(v))}
            placeholder="基準日"
            onEnter={onSearch}
          />
        </div>

        <div>
          <label htmlFor="search_title" className="mb-1 block text-sm text-gray-600">
            タイトル
          </label>
          <SearchTextField
            id="search_title"
            value={searchTitle}
            placeholder="タイトル"
            onChange={e => onChangeSearchTitle(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.stopPropagation();
                onSearch();
              }
            }}
          />
        </div>

        {canUseSiteTypeFeatures && (
          <div>
            <label htmlFor="search_contents" className="mb-1 block text-sm text-gray-600">
              お知らせ内容
            </label>
            <SearchTextField
              id="search_contents"
              value={searchContents}
              placeholder="お知らせ内容"
              onChange={e => onChangeSearchContents(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.stopPropagation();
                  onSearch();
                }
              }}
            />
          </div>
        )}

        {!canUseSiteTypeFeatures && (
          <div>
            <label htmlFor="search_contents" className="mb-1 block text-sm text-gray-600">
              お知らせ内容
            </label>
            <SearchTextField
              id="search_contents"
              value={searchContents}
              placeholder="お知らせ内容"
              onChange={e => onChangeSearchContents(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.stopPropagation();
                  onSearch();
                }
              }}
            />
          </div>
        )}
      </div>

      {/* Row 2: last 3 inputs + buttons */}
      <div className="grid gap-4 grid-cols-4 items-end">
        {canUseSiteTypeFeatures && (
          <div>
            <label htmlFor="search_site_type" className="mb-1 block text-sm text-gray-600">
              表示対象
            </label>
            <AppMultiSelect
              id="search_site_type"
              placeholder="サイト区分"
              value={siteTypes}
              style={{ width: '100%' }}
              maxTagCount={hasAllSiteTypesSelected ? 0 : 'responsive'}
              maxTagPlaceholder={hasAllSiteTypesSelected ? () => 'すべて' : undefined}
              showSearch
              onInputKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              options={[
                { label: 'すべて', value: 'all' },
                ...tenants.map(t => ({ label: t.name, value: t.site_type })),
                { label: '個別治療院', value: 'individual_clinic' },
              ]}
              onChange={values => {
                const next = (values as string[]) || [];
                if (next.includes('all')) {
                  onChangeSiteTypes([...tenantSiteTypes, 'individual_clinic']);
                  return;
                }
                onChangeSiteTypes(next.filter(v => v !== 'all'));
              }}
            />
          </div>
        )}

        <div>
          <label htmlFor="search_service_type" className="mb-1 block text-sm text-gray-600">
            表示サービス
          </label>
          <AppMultiSelect
            id="search_service_type"
            placeholder="表示サービス"
            value={serviceTypes}
            style={{ width: '100%' }}
            maxTagCount={hasAllServiceTypesSelected ? 0 : 'responsive'}
            maxTagPlaceholder={hasAllServiceTypesSelected ? () => 'すべて' : undefined}
            onInputKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            options={SERVICE_TYPE_OPTIONS}
            onChange={values => {
              const next = (values as string[]) || [];
              if (next.includes('all')) {
                onChangeServiceTypes(allServiceValues);
                return;
              }
              onChangeServiceTypes(next.filter(v => v !== 'all'));
            }}
          />
        </div>

        <div className="col-start-4 flex items-end gap-2 justify-start">
          <AppButton
            onClick={onSearch}
            type="primary"
            icon={<Search />}
            style={{ backgroundColor: colors.secondary, borderColor: colors.secondary }}
            className="py-1 px-4 text-white h-auto btn-ra2-hover font-medium !h-[40px]"
          >
            検索
          </AppButton>
          <AppButton
            onClick={onCsv}
            type="primary"
            icon={<Download />}
            style={{ backgroundColor: colors.primary, borderColor: colors.primary }}
            className="py-1 px-4 text-white h-auto btn-ra2-hover font-medium !h-[40px]"
          >
            CSV
          </AppButton>
        </div>
      </div>
    </div>
  );
};

export default InformationsFilterPanel;
